import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ICustomFieldTargetColumnsContainer, ICustomFieldTargetColumnsSettings } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as PM from 'app/pm/models/essette-api';

@autoinject()
export class MemberCampaigns extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected memberCampaigns: ReadonlyArray<PM.Candidate> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly memberService: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.loadCustomFieldTargets();
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadCandidates();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    protected isLatestHistory(status: PM.CandidateStatusHistory): boolean {
        return status?.metadata?.ended?.timestamp === null;
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        const settings: ICustomFieldTargetColumnsSettings = {
            definitionCount: 10
        };
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('PM.Candidate', settings);
    }

    @apiCall
    private async loadCandidates(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes: string[] = [
            'campaign',
            'custom',
            'status',
            'assignedTo',
            'history.statuses',
            'metadata.created.byUser'
        ];

        const fields = [
            'assignedTo.description',
            'campaign.description',
            'custom.lookup.*',
            'custom.text.*',
            'history.statuses.metadata.ended.timestamp',
            'history.statuses.metadata.started.timestamp',
            'metadata.closed.timestamp',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'number',
            'status.description'
        ];

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadCandidatesByMember(this.memberId, options, includes, fields);
            this.memberCampaigns = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
